import React from "react";
import { createStore, combineReducers, applyMiddleware, compose } from "redux";
import { fireEvent, waitFor, within } from "@testing-library/react";
import { createAndConfigureStore } from "store";
import { connect, Provider } from "react-redux";
import { entitiesReducer as entities } from "redux-query";
import thunk from "redux-thunk";
import api from "middleware/api";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import theme from "styles/theme";
import { AjaxResponse } from "rxjs";
import Snackbars from "components/snackbars";
import reducers, { IStore } from "reducers";
import { RowClassParams, RowNode } from "ag-grid-community";
import { IGridItem } from "./legacy-cart/line-items/items-conversion-helper";
import _ from "lodash";

export function withAppStore(comp) {
    const store = createAndConfigureStore();
    return <Provider store={store}>{comp}</Provider>;
}

export const withStore = (stubReducers) => (component) => {
    const store = createStore(
        stubReducers ? combineReducers({ entities, ...stubReducers }) : reducers,
        compose(applyMiddleware(thunk, api))
    );
    return <Provider store={store}>{component}</Provider>;
};

export function withMuiThemeProvider(comp) {
    return <MuiThemeProvider muiTheme={theme}>{comp}</MuiThemeProvider>;
}

export function wrapAsMockedFetchJsonResponse(jsonPayload: any): Partial<Response> {
    return {
        ok: true,
        headers: {
            get: jest.fn(),
        } as any,
        json: () => Promise.resolve(jsonPayload),
    };
}

export function wrapAsMockedRxjsResponse(payload: any): Partial<AjaxResponse> {
    return {
        xhr: {
            getResponseHeader: jest.fn(),
        } as any,
        response: payload,
        responseText: null,
        responseType: "json",
    };
}

export function withSnackbars(comp) {
    const ComponentWithSnackbars = ({ snackbars }) => (
        <>
            {comp}
            <Snackbars snackbars={snackbars} onClearSnackbar={() => null} />
        </>
    );

    const ConnectedComponentWithSnackbars = connect((state: IStore) => ({ snackbars: state.snackbars }))(
        ComponentWithSnackbars
    );

    return <ConnectedComponentWithSnackbars />;
}

export function materialUiDropdwonOrSelectClick(element: HTMLElement) {
    fireEvent.click(within(element).getByRole("button"));
}

/**
 * Accepts arguments of the console method.
 * Returns `true` if the call should be suppressed.
 */
type SuppressPredicate = (...args: any[]) => boolean;
type SuppressDesc = RegExp | string | SuppressPredicate;

interface SuppressConsoleOptions {
    // blacklists by console methods
    warn?: SuppressDesc[];
    error?: SuppressDesc[];
    info?: SuppressDesc[];
    log?: SuppressDesc[];

    // lib related shortcuts
    /** Suppress warnings about outdated `act` method before react-dom@16.9.0. */
    reactVersionWarning?: boolean;
    /** Suppress warnings about deprecated moment.js initializer argument, which are widely used in the legacy code. */
    momentJsDeprecatedArg?: boolean;
    /** Suppress warnings about deprecated material-ui `css` function, which is hugely used in the app. */
    materialUiCss?: boolean;
    /** Suppress warnings about unsafe lyfecicle methods from external libraries like MUI after React upgrade to 16.10.2. */
    unsafeLifecycleMethods?: boolean;
    /** Suppress error messages about missing AgGrid license */
    agGridLicense?: boolean;
    /** Suppress error messages about invalid aria- attributes in AgGrid */
    agGridAriaWarning?: boolean;
}

export function suppressConsole({
    error,
    warn,
    info,
    log,
    reactVersionWarning = true,
    momentJsDeprecatedArg = true,
    materialUiCss = true,
    unsafeLifecycleMethods = true,
    agGridLicense = true,
    agGridAriaWarning = true,
}: SuppressConsoleOptions = {}) {
    // these blacklist entries to be removed once we upgrade react to 16.9 or higher.
    if (reactVersionWarning) {
        (error || (error = [])).push(
            "Please upgrade to at least react-dom@16.9.0",
            "React state updates should be wrapped into act",
            "An update to %s inside a test was not wrapped in act"
        );
    }

    // legacy code massively uses something like momemt('1932-09-06') which is not a proper initialization value.
    if (momentJsDeprecatedArg) {
        (warn || (warn = [])).push("Deprecation warning: value provided is not in a recognized RFC2822 or ISO format.");
    }

    if (materialUiCss) {
        (warn || (warn = [])).push("Material-UI: The `css` function is deprecated. Use the `styleFunctionSx` instead");
    }

    if (unsafeLifecycleMethods) {
        (warn || (warn = [])).push(
            /(?:componentWillMount|componentWillReceiveProps|componentWillUpdate) has been renamed/
        );
    }

    if (agGridLicense) {
        (error || (error = [])).push(() => {
            const { stack } = new Error();
            return stack && stack.includes("ag-grid") && stack.includes("outputMissingLicenseKey");
        });
    }
    if (agGridAriaWarning) {
        (error || (error = [])).push('"Warning: Invalid aria prop %s"');
    }

    for (const [method, blacklist] of Object.entries({ error, warn, info, log })) {
        if (!blacklist?.length) {
            continue;
        }

        // eslint-disable-next-line no-console
        const originalError = console[method];

        beforeAll(() => {
            jest.spyOn(console, method as any).mockImplementation((...args: any[]) => {
                if (
                    blacklist.some(
                        (desc) =>
                            (typeof desc === "function" && desc(...args)) ||
                            (typeof desc === "string" && args.some((arg) => `${arg}`.includes(desc))) ||
                            (desc instanceof RegExp && args.some((arg) => desc.test(arg)))
                    )
                ) {
                    return;
                }
                return originalError.call(console, ...args);
            });
        });

        afterAll(() => {
            // eslint-disable-next-line no-console
            (console[method] as jest.Mock).mockRestore();
        });
    }
}

export function mockGlobalAnalytics(onEach = true) {
    let initial: any;
    let track: jest.Mock;

    const [before, after] = onEach ? [beforeEach, afterEach] : [beforeAll, afterAll];

    before(() => {
        track = jest.fn();
        initial = global.analytics;
        global.analytics = { track } as any;
    });

    after(() => {
        global.analytics = initial;
    });
}

export const waitForGridToBeInTheDOM = () => {
    return waitFor(() => {
        expect(document.querySelector(".ag-root-wrapper")).toBeInTheDocument();
    });
};

/**
 * Creates list of class names using row grouping fields,
 * omits non-string symbols from key-values, joins them by '-' and moves them to lowercase
 * (ex. ['groupfield1-value1', 'groupfield2-value2'])
 * @param {RowClassParams} params - RowClassParams
 * @returns list of class names
 */
export const getRowClasses = (params: CastProp<RowClassParams, "data", IGridItem>) => {
    const normalizeClass = (str: string) => _.toLower(str?.replace(/[^\w\d]/g, "") ?? "");
    const composeClassName = (node: CastProp<RowNode, "data", IGridItem>) => {
        const classNames = [];
        if (node.group) {
            classNames.push([node.field, node.key].map(normalizeClass).join("-"));
        } else {
            classNames.push(["productId", node.data.productId].map(normalizeClass).join("-"));
        }
        if (node.level > 0) {
            classNames.push(...composeClassName(node.parent));
        }
        return classNames;
    };
    return composeClassName(params.node);
};
